import React, { useState } from 'react';
import Spinner from './Spinner';
import SelkirkIcon from '../icons/SelkirkIcon';
import { ICON } from '../../icons/iconIds';
import './Table.css';

const Table = ({
  data = [],
  columns = [],
  sortable = true,
  hoverable = true,
  striped = false,
  compact = false,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
  onRowClick,
  ...props
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const handleSort = (key) => {
    if (!sortable) return;

    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return (
        <span className="sort-icon">
          <SelkirkIcon name={ICON.uiSortNeutral} size={14} decorative />
        </span>
      );
    }
    return (
      <span className="sort-icon active">
        <SelkirkIcon
          name={sortConfig.direction === 'asc' ? ICON.uiSortAsc : ICON.uiSortDesc}
          size={14}
          decorative
        />
      </span>
    );
  };

  const tableClasses = [
    'table',
    hoverable && 'table-hoverable',
    striped && 'table-striped',
    compact && 'table-compact',
    className
  ].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className="table-container">
        <div className="table-loading">
          <Spinner size="medium" />
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="table-container">
        <div className="table-empty">
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="table-container" {...props}>
      <table className={tableClasses}>
        <thead className="table-header">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`table-header-cell ${sortable && column.sortable !== false ? 'sortable' : ''}`}
                style={{ width: column.width }}
                onClick={() => column.sortable !== false && handleSort(column.key)}
              >
                <div className="header-content">
                  <span>{column.title}</span>
                  {sortable && column.sortable !== false && getSortIcon(column.key)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table-body">
          {sortedData.map((row, index) => (
            <tr
              key={row.id || index}
              className={`table-row ${onRowClick ? 'clickable' : ''}`}
              onClick={() => onRowClick?.(row, index)}
            >
              {columns.map((column) => (
                <td key={column.key} className="table-cell">
                  {column.render ? column.render(row[column.key], row, index) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
